import type { ExportMarket } from './exportTypes';

export const PAGE_CANDLES = 10_000;
export const MAX_ROWS_TOTAL = 5_000_000;

export const GRID_ORIGIN_MS = Date.UTC(2000, 0, 3);

const MINUTE = 60_000;

export interface TimeWindow {
    startMs: number;
    endMs: number;
}

export function alignDown(ms: number, intervalMs: number): number {
    return (
        GRID_ORIGIN_MS +
        Math.floor((ms - GRID_ORIGIN_MS) / intervalMs) * intervalMs
    );
}

export function alignUp(ms: number, intervalMs: number): number {
    return (
        GRID_ORIGIN_MS +
        Math.ceil((ms - GRID_ORIGIN_MS) / intervalMs) * intervalMs
    );
}

export function gridWindow(
    fromMs: number,
    toMs: number,
    intervalMinutes: number,
    nowMs: number
): TimeWindow | null {
    const intervalMs = intervalMinutes * MINUTE;
    const startMs = alignDown(fromMs, intervalMs);
    const endMs = alignUp(Math.min(toMs, nowMs), intervalMs);
    return endMs > startMs ? { startMs, endMs } : null;
}

export function rowsIn(window: TimeWindow, intervalMinutes: number): number {
    return Math.round(
        (window.endMs - window.startMs) / (intervalMinutes * MINUTE)
    );
}

export function pagesOf(
    window: TimeWindow,
    intervalMinutes: number,
    pageCandles: number = PAGE_CANDLES
): TimeWindow[] {
    const step = pageCandles * intervalMinutes * MINUTE;
    const pages: TimeWindow[] = [];
    for (let start = window.startMs; start < window.endMs; start += step) {
        pages.push({
            startMs: start,
            endMs: Math.min(window.endMs, start + step),
        });
    }
    return pages;
}

export function pageCount(
    window: TimeWindow,
    intervalMinutes: number,
    pageCandles: number = PAGE_CANDLES
): number {
    return Math.ceil(
        (window.endMs - window.startMs) /
            (pageCandles * intervalMinutes * MINUTE)
    );
}

export function marketWindow(
    window: TimeWindow,
    market: ExportMarket,
    intervalMinutes: number
): TimeWindow | null {
    if (market.fromMs === undefined && market.toMs === undefined) {
        return window;
    }
    const intervalMs = intervalMinutes * MINUTE;
    const startMs =
        market.fromMs === undefined
            ? window.startMs
            : Math.max(window.startMs, alignDown(market.fromMs, intervalMs));
    const endMs =
        market.toMs === undefined
            ? window.endMs
            : Math.min(window.endMs, alignUp(market.toMs, intervalMs));
    return endMs > startMs ? { startMs, endMs } : null;
}

export type ExportBlock = 'empty' | 'total' | null;

export interface ExportEstimate {
    rowsPerMarket: number;
    rowsTotal: number;
    requests: number;
    files: number;
    blocked: ExportBlock;
}

export function estimate(
    window: TimeWindow | null,
    intervalMinutes: number,
    markets: readonly ExportMarket[],
    oneFile: boolean
): ExportEstimate {
    const windows = window
        ? markets.flatMap(market => {
              const own = marketWindow(window, market, intervalMinutes);
              return own ? [own] : [];
          })
        : [];
    if (windows.length === 0) {
        return {
            rowsPerMarket: 0,
            rowsTotal: 0,
            requests: 0,
            files: 0,
            blocked: 'empty',
        };
    }
    let rowsPerMarket = 0;
    let rowsTotal = 0;
    let requests = 0;
    for (const own of windows) {
        const rows = rowsIn(own, intervalMinutes);
        rowsPerMarket = Math.max(rowsPerMarket, rows);
        rowsTotal += rows;
        requests += pageCount(own, intervalMinutes);
    }
    return {
        rowsPerMarket,
        rowsTotal,
        requests,
        files: oneFile ? 1 : windows.length,
        blocked: rowsTotal > MAX_ROWS_TOTAL ? 'total' : null,
    };
}
